<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { isToday, isPast } from 'date-fns';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import TaskDetailsContent from 'dashboard/components-next/Tasks/TaskDetailsContent.vue';
import {
  getTaskDueDate,
  formatTaskEndTime,
} from 'dashboard/helper/taskRecurrence';

const props = defineProps({
  task: { type: Object, required: true },
  isExpanded: { type: Boolean, default: false },
  isUpdating: { type: Boolean, default: false },
  isDeleting: { type: Boolean, default: false },
});

const emit = defineEmits([
  'toggle',
  'toggleStatus',
  'updateTask',
  'deleteTask',
]);

const { t } = useI18n();

const dueDate = computed(() => getTaskDueDate(props.task));

const isCompleted = computed(() => props.task.status === 'completed');

const urgencyClass = computed(() => {
  if (isCompleted.value) return 'text-n-slate-10';
  if (isPast(dueDate.value) && !isToday(dueDate.value)) return 'text-n-ruby-9';
  if (isToday(dueDate.value)) return 'text-n-teal-9';
  return 'text-n-slate-11';
});

const linkedEntityLabel = computed(() => {
  if (props.task.contact) return props.task.contact.name;
  if (props.task.company) return props.task.company.name;
  if (props.task.deal) return props.task.deal.name;
  return '';
});

const formattedDueDate = computed(() => {
  const options = props.task.allDay
    ? { day: '2-digit', month: 'short', year: 'numeric' }
    : {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      };
  return `${new Intl.DateTimeFormat('pt-BR', options).format(dueDate.value)}${formatTaskEndTime(props.task, 'pt-BR')}`;
});

const assigneeLabel = computed(() => {
  if (!props.task.assignedTo) return '';
  const { name, active } = props.task.assignedTo;
  return active === false
    ? `${name} ${t('TASKS.DEACTIVATED_AGENT_SUFFIX')}`
    : name;
});

const onClickExpand = () => emit('toggle');
</script>

<template>
  <div class="relative">
    <CardLayout layout="row">
      <div class="flex items-center flex-1 min-w-0 gap-4">
        <span
          class="flex-1 min-w-0 truncate text-sm font-medium"
          :class="
            isCompleted ? 'line-through text-n-slate-10' : 'text-n-slate-12'
          "
        >
          {{ task.title }}
        </span>
        <span class="flex-1 min-w-0 truncate text-sm text-n-slate-11">
          {{ linkedEntityLabel }}
        </span>
        <span class="w-40 shrink-0 text-sm" :class="urgencyClass">
          {{ formattedDueDate }}
        </span>
        <span class="w-32 shrink-0 truncate text-sm text-n-slate-11">
          {{ assigneeLabel }}
        </span>
      </div>

      <Button
        icon="i-lucide-chevron-down"
        variant="ghost"
        color="slate"
        size="xs"
        class="shrink-0"
        :class="{ 'rotate-180': isExpanded }"
        @click="onClickExpand"
      />

      <template #after>
        <div
          class="transition-all duration-500 ease-in-out grid overflow-hidden"
          :class="
            isExpanded
              ? 'grid-rows-[1fr] opacity-100'
              : 'grid-rows-[0fr] opacity-0'
          "
        >
          <div class="overflow-hidden">
            <TaskDetailsContent
              :task="task"
              :is-updating="isUpdating"
              :is-deleting="isDeleting"
              @toggle-status="emit('toggleStatus')"
              @update-task="emit('updateTask', $event)"
              @delete-task="(id, options) => emit('deleteTask', id, options)"
            />
          </div>
        </div>
      </template>
    </CardLayout>
  </div>
</template>
